import {
    DV360_CHANNEL, FACEBOOK_CHANNEL, GOOGLE_CHANNEL, LINKEDIN_CHANNEL, TIKTOK_CHANNEL,
} from '../channels/channels';

/**
 * La regla de cruce paid media ↔ GA4, sin React: qué canales tienen regla, cómo se lee del backend,
 * cuándo un borrador difiere de lo guardado y qué opciones se pueden elegir en cada nivel.
 *
 * Contrato (tanda «Cruce paid media ↔ GA4», `contrato.md` § 0 y § 2): la regla viaja traducida, por
 * nivel, como `adgroup_param` / `ad_param` ∈ `'utm_term' | 'utm_content' | null` y `match_by` ∈
 * `'name' | 'id'`; Google llega con `mode: 'AUTO_GOOGLE_ADS_LINK'` y sin parámetros (se cruza solo,
 * por la vinculación de Google Ads con GA4).
 */

/** Las plataformas con nivel de grupo de anuncios, en el orden en que se muestran. */
export const ATTRIBUTION_CHANNELS = [GOOGLE_CHANNEL, FACEBOOK_CHANNEL, TIKTOK_CHANNEL, LINKEDIN_CHANNEL, DV360_CHANNEL];

export const PARAM_TERM = 'utm_term';
export const PARAM_CONTENT = 'utm_content';
export const PARAMS = [PARAM_TERM, PARAM_CONTENT];

export const MATCH_NAME = 'name';
export const MATCH_ID = 'id';
export const MATCH_OPTIONS = [MATCH_NAME, MATCH_ID];

export const LEVEL_ADGROUP = 'adgroup';
export const LEVEL_AD = 'ad';

export const MODE_AUTO_GOOGLE = 'AUTO_GOOGLE_ADS_LINK';

/** La tarjeta de la agencia en Conexiones (2.1): a donde llevan «Ver la regla» y «Cambiar». */
export const AGENCY_CARD_URL = '/agency/conections#atribucion-ga4';

export const EMPTY_RULE = Object.freeze({adgroup_param: null, ad_param: null, match_by: MATCH_NAME});

const knownParam = (value) => (PARAMS.includes(value) ? value : null);

export function isKnownChannel(channel) {
    return ATTRIBUTION_CHANNELS.includes(channel);
}

/** Google no se configura: GA4 recibe el grupo y el anuncio por la vinculación con Google Ads. */
export function isAutomatic(channel, rule) {
    return channel === GOOGLE_CHANNEL || Boolean(rule && rule.mode === MODE_AUTO_GOOGLE);
}

/**
 * La regla de un nivel tal como la entiende la pantalla. Un parámetro que el FE no conoce se lee como
 * «no cruzar» (un backend más nuevo podría sumar uno): mostrarlo elegido en un `<select>` que no lo
 * tiene dejaría el control en blanco.
 */
export function ruleFromPayload(payload) {
    if (!payload) return {...EMPTY_RULE};
    return {
        adgroup_param: knownParam(payload.adgroup_param),
        ad_param: knownParam(payload.ad_param),
        match_by: payload.match_by === MATCH_ID ? MATCH_ID : MATCH_NAME,
    };
}

/** Los dos niveles en «no cruzar». */
export function isNoneRule(rule) {
    return !rule || (!rule.adgroup_param && !rule.ad_param);
}

/**
 * ¿El borrador difiere de lo guardado? Con los dos niveles en «no cruzar», `match_by` no significa
 * nada (el control está deshabilitado), así que no ensucia.
 */
export function isDirty(saved, draft) {
    const a = ruleFromPayload(saved);
    const b = ruleFromPayload(draft);
    if (a.adgroup_param !== b.adgroup_param || a.ad_param !== b.ad_param) return true;
    if (isNoneRule(a)) return false;
    return a.match_by !== b.match_by;
}

/** «Cruza por» sólo tiene sentido si algún nivel se cruza. */
export function matchByEnabled(rule) {
    return !isNoneRule(rule);
}

/**
 * Las opciones del `<select>` de un nivel: los dos parámetros y «no cruzar» (`null`). Un parámetro que
 * ya usa el OTRO nivel va deshabilitado: el mismo `utm_term` no puede decir a la vez de qué conjunto y
 * de qué anuncio viene una sesión.
 */
export function availableParams(level, rule) {
    const current = ruleFromPayload(rule);
    const other = level === LEVEL_AD ? current.adgroup_param : current.ad_param;
    return [...PARAMS.map((param) => ({param, disabled: param === other})), {param: null, disabled: false}];
}

/**
 * Cambia un campo del borrador. Si el parámetro elegido es el que usaba el otro nivel, el otro queda
 * en «no cruzar»: el `<select>` ya lo ofrece deshabilitado, esto es la red por si llega igual.
 */
export function withField(rule, field, value) {
    const next = {...ruleFromPayload(rule), [field]: value};
    if (field === 'adgroup_param' && value && next.ad_param === value) next.ad_param = null;
    if (field === 'ad_param' && value && next.adgroup_param === value) next.adgroup_param = null;
    return ruleFromPayload(next);
}

/**
 * ¿Vale la pena mostrar la sugerencia del backend para este canal? Sólo si propone algo distinto de
 * lo que la fila tiene ahora (el borrador): después de «Usar esta regla» el aviso desaparece, y la
 * fila queda sin guardar hasta que se toque «Guardar».
 *
 * Una sugerencia por ID también se muestra, con su propio texto («trae el ID de sus…», aprobado por
 * fede el 16/10; ver `suggestionTextKey`). Hasta ese día se escondía porque el único texto decía
 * «coincide con los NOMBRES» (review C-regla-frontend-03).
 */
export function suggestionApplies(suggestion, draft) {
    if (!suggestion || !suggestion.rule) return false;
    if (!isKnownChannel(suggestion.channel) || suggestion.channel === GOOGLE_CHANNEL) return false;
    const proposed = ruleFromPayload(suggestion.rule);
    if (isNoneRule(proposed)) return false;
    return isDirty(draft, proposed);
}

/** La clave del texto del aviso: por ID dice «trae el ID», por nombre «coincide con los nombres». */
export function suggestionTextKey(suggestion) {
    const proposed = ruleFromPayload((suggestion && suggestion.rule) || {});
    return proposed.match_by === MATCH_ID ? 'ga4_attribution.agency.suggestion_id' : 'ga4_attribution.agency.suggestion';
}

/**
 * El GET de la agencia, normalizado: canales desconocidos fuera, en el orden de
 * `ATTRIBUTION_CHANNELS`, y `canEdit` abierto si el backend no manda la clave (el 403 del PUT sigue
 * siendo la autoridad; patrón `AgencyVoiceSettings.resolveCanEdit`).
 *
 * `hasGa4Connection` sólo es verdadero si el backend lo dice (`=== true`): un backend anterior a la
 * UD-10 no manda la clave, y sin ella el bloque de la conexión de GA4 no se dibuja.
 */
export function agencyRulesFromPayload(payload) {
    const body = payload || {};
    const rules = (Array.isArray(body.rules) ? body.rules : [])
        .filter((rule) => rule && isKnownChannel(rule.channel))
        .sort((a, b) => ATTRIBUTION_CHANNELS.indexOf(a.channel) - ATTRIBUTION_CHANNELS.indexOf(b.channel));
    return {
        canEdit: body.can_edit !== false,
        hasGa4Connection: body.has_ga4_connection === true,
        rules,
        suggestions: (Array.isArray(body.suggestions) ? body.suggestions : [])
            .filter((suggestion) => suggestion && isKnownChannel(suggestion.channel)),
        overrides: (Array.isArray(body.overrides) ? body.overrides : [])
            .filter((override) => override && isKnownChannel(override.channel)),
    };
}

/**
 * Las plataformas que el bloque de la conexión de GA4 lista (UD-10): las que tienen alguna cuenta de
 * pauta con una propiedad de GA4 vinculada (`ga4_linked_accounts > 0`). Google entra con la misma
 * regla, como su línea fija. Lo demás no se muestra: a una agencia sin cuentas de LinkedIn vinculadas
 * no le importa cómo se etiqueta LinkedIn.
 */
export function linkedRules(rules) {
    return (rules || []).filter((rule) => Number(rule.ga4_linked_accounts) > 0);
}

/**
 * ¿La línea de una plataforma muestra la sugerencia como acción principal? Sólo si lo guardado es
 * «No cruzar» y la sugerencia propone algo: con una regla ya puesta, cambiarla es de «Cambiar».
 */
export function suggestionOffered(suggestion, saved) {
    return isNoneRule(saved) && suggestionApplies(suggestion, saved);
}

/** `{canal: regla}` con lo que tiene guardado cada fila. */
export function savedRulesByChannel(rules) {
    const byChannel = {};
    (rules || []).forEach((rule) => {
        byChannel[rule.channel] = ruleFromPayload(rule);
    });
    return byChannel;
}

/** Una fila se edita si el usuario puede, el backend la marca editable y no es la de Google. */
export function rowIsEditable(rule, canEdit) {
    return Boolean(canEdit) && rule.editable !== false && !isAutomatic(rule.channel, rule);
}

/**
 * La línea, después de guardar la regla de su cuenta: lo que el modal recibió del PUT (el mismo
 * shape del GET de la cuenta) traducido al `ga4_attribution` de solo lectura de la línea
 * (`contrato.md` § 4), sin pedir la línea de nuevo.
 */
export function lineAttributionFromAccountRule(payload, previous) {
    const base = previous || {};
    if (!payload) return base;
    const custom = payload.mode === 'custom' && payload.custom_rule;
    const rule = ruleFromPayload(custom ? payload.custom_rule : payload.agency_rule);
    let source = custom ? 'account' : 'agency';
    if (isAutomatic(payload.channel || base.channel, payload.agency_rule)) source = 'auto_google';
    else if (isNoneRule(rule)) source = 'none';
    return {...base, ...rule, source};
}
